import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { Menu as MenuIcon, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import SidebarCst from "@components/SidebarCst";
import { SidebarSection } from "@components/SidebarCst/types";
import { CreatePickerHost } from "@components/CreateOptionPickerCst/host";
import { EnrollmentModalHost } from "@pages/Contents/V2/Corporate/Catalog/Enrollment/host";
import { AssociateInitiativesHost } from "@pages/Contents/V2/Corporate/Catalog/AssociateInitiatives/host";
import { UsersAndGroupsHost } from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/host";
import { EditionEnrollmentHost } from "@pages/Contents/V2/Corporate/Catalog/Editions/EditionEnrollment/host";
import { SessionWizardHost } from "@pages/Contents/V2/Corporate/Catalog/Editions/SessionWizard/host";
import ToastHostCst from "@components/ToastHostCst";
import { useNavigationRoutesV2 } from "@hooks/useNavigationRoutesV2";
import { useFetchDataDetail, useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";

const CorporateDetailV2Layout: React.FC = () => {
  const { t } = useTranslation("sidebar");
  const { id = "" } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("lg"));
  const isAutoCollapse = useMediaQuery(theme.breakpoints.between("lg", "xl"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    setSidebarCollapsed(isAutoCollapse);
  }, [isAutoCollapse]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const { dataDetail } = useFetchDetail(
    ServicesURL.corporate,
    id,
    PagesID.corporate,
  );

  const { dataDetail: initiativesData } = useFetchDataDetail(
    id ? `${ServicesURL.initiative}/${id}/initiatives?status=ACTIVE` : null,
    PagesID.corporate,
  );

  const [selectedInitiative, setSelectedInitiative] = useState("all");

  const initiativeOptions = useMemo(() => {
    const allOption = { label: t("all-initiatives") || "", value: "all" };
    const initiatives = (initiativesData?.output as any[] | undefined) || [];
    return [
      allOption,
      ...initiatives.map((init: any) => ({
        label: init.name || init.title || `${init.id}`,
        value: String(init.id),
      })),
    ];
  }, [initiativesData, t]);

  const baseUrl = `/v2/corporate/${id}/detail`;

  const isActive = (segment: string) =>
    location.pathname === `${baseUrl}/${segment}` ||
    location.pathname.startsWith(`${baseUrl}/${segment}/`);

  const isOverviewActive =
    location.pathname === baseUrl || location.pathname === `${baseUrl}/`;

  const navV2 = useNavigationRoutesV2(dataDetail);

  const sidebarSections: SidebarSection[] = useMemo(() => {
    const fromNav: SidebarSection[] = navV2.map((section) => ({
      label: section.label,
      type: section.type,
      items: section.items
        .filter((i) => i.kind === "card")
        .map((item) => ({
          icon: item.icon,
          title: item.title,
          description: item.description,
          to: item.to ? `${baseUrl}/${item.to}` : undefined,
          active: item.to ? isActive(item.to) : false,
          actionButton: item.actionButton,
          children: item.children?.map((c) => ({
            title: c.title,
            to: c.to ? `${baseUrl}/${c.to}` : undefined,
          })),
        })),
      simpleItems: section.items
        .filter((i) => i.kind === "simpleItem")
        .map((item) => ({
          icon: item.icon,
          title: item.title,
          description: item.description,
          to: item.to ? `${baseUrl}/${item.to}` : undefined,
          hasChevron: item.hasChevron,
          children: item.children?.map((c) => ({
            title: c.title,
            to: c.to ? `${baseUrl}/${c.to}` : undefined,
          })),
        })),
    }));

    // Voci hardcoded ancora non migrate nel hook useNavigationRoutesV2.
    // Restano commentate come riferimento; verranno progressivamente
    // spostate nel hook insieme alle relative logiche di permission / flag.
    /*
    const overviewCard = {
      label: t("sections.menu") || "",
      type: "cards" as const,
      items: [
        {
          icon: <LayoutGrid size={18} />,
          title: t("cards.overview.title") || "",
          description: t("cards.overview.description") || "",
          active: isOverviewActive,
          to: baseUrl,
        },
        {
          icon: <Users size={18} />,
          title: t("cards.users-groups.title") || "",
          active: isActive("users"),
          actionButton: {
            label: t("create-button") || "",
            onClick: () => {},
          },
          to: `${baseUrl}/users`,
        },
        {
          icon: <Bell size={18} />,
          title: t("cards.notifications.title") || "",
          description: t("cards.notifications.description") || "",
          active: isActive("notifications"),
          to: `${baseUrl}/notifications`,
        },
        {
          icon: <BarChart3 size={18} />,
          title: t("cards.monitoring.title") || "",
          description: t("cards.monitoring.description") || "",
          active: isActive("dashboard"),
          to: `${baseUrl}/dashboard`,
        },
      ],
    };

    const otherProcesses = {
      label: t("sections.other-processes") || "",
      type: "cards" as const,
      items: [
        {
          icon: <MoreHorizontal size={18} />,
          title: t("items.other-processes.title") || "",
          description: t("items.other-processes.description") || "",
          children: [
            { title: "Self Signup", to: `${baseUrl}/selfsignup` },
            { title: "Campagne Raccolta", to: `${baseUrl}/collect-campaign` },
            { title: "Formazione Finanziata", to: `${baseUrl}/funded-training` },
          ],
        },
      ],
    };

    const otherTools = {
      label: t("sections.other-tools") || "",
      type: "simple" as const,
      items: [],
      simpleItems: [
        {
          icon: <Wrench size={18} />,
          title: t("items.configure-platform.title") || "",
          hasChevron: true,
          children: [
            { title: "Mestieri e Skill", to: `${baseUrl}/skills` },
            { title: "Badge Certificati e Skillbook", to: `${baseUrl}/badge` },
            { title: "Sedi e Aule", to: `${baseUrl}/locations` },
            { title: "Edizioni Aula Fisica", to: `${baseUrl}/physical-editions` },
            { title: "Edizioni Aula Virtuale", to: `${baseUrl}/virtual-editions` },
            { title: "Media Library", to: `${baseUrl}/media-library` },
            { title: "Termini Vietati", to: `${baseUrl}/forbidden-terms` },
          ],
        },
      ],
    };

    const preferences = {
      label: t("sections.preferences") || "",
      type: "simple" as const,
      items: [],
      simpleItems: [
        {
          icon: <Settings size={18} />,
          title: t("items.settings.title") || "",
          hasChevron: false,
        },
      ],
    };
    */

    return fromNav;
  }, [navV2, baseUrl, location.pathname]);

  const desktopSidebar = (
    <SidebarCst
      header={{
        corporateName: dataDetail?.name || "Corporate",
        initiativeLabel: t("initiative-label") || "",
        initiativeOptions,
        selectedInitiative,
        onInitiativeChange: setSelectedInitiative,
      }}
      sections={sidebarSections}
      collapsed={sidebarCollapsed}
      onExpandRequest={() => setSidebarCollapsed(false)}
    />
  );

  // Drawer (mobile) always renders the expanded version.
  const drawerSidebar = (
    <SidebarCst
      header={{
        corporateName: dataDetail?.name || "Corporate",
        initiativeLabel: t("initiative-label") || "",
        initiativeOptions,
        selectedInitiative,
        onInitiativeChange: setSelectedInitiative,
      }}
      sections={sidebarSections}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        gap: "16px",
        padding: "16px",
        backgroundColor: "background.default",
      }}
    >
      {!isCompact && (
        <Box sx={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
          {desktopSidebar}
          <IconButton
            onClick={() => setSidebarCollapsed((v) => !v)}
            aria-label={
              sidebarCollapsed ? "expand sidebar" : "collapse sidebar"
            }
            sx={{
              width: "40px",
              height: "40px",
              marginTop: "16px",
              borderRadius: "10px",
              backgroundColor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.primary,
              flexShrink: 0,
              transition:
                "background-color 0.15s ease, border-color 0.15s ease",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
                borderColor: theme.palette.text.tertiary,
              },
            }}
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen size={20} />
            ) : (
              <PanelLeftClose size={20} />
            )}
          </IconButton>
        </Box>
      )}

      {isCompact && (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
              padding: "16px",
            },
          }}
        >
          {drawerSidebar}
        </Drawer>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {isCompact && (
          <Box sx={{ mb: 2 }}>
            <IconButton
              onClick={() => setDrawerOpen(true)}
              aria-label="open menu"
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "8px",
                color: theme.palette.text.primary,
              }}
            >
              <MenuIcon size={20} />
            </IconButton>
          </Box>
        )}
        <Container maxWidth={"xxl"} sx={{ px: { xs: 0, md: 0 } }}>
          <Outlet
            context={{
              selectedInitiative,
              setSelectedInitiative,

              selectedInitiativeName:
                initiativeOptions.find((o) => o.value === selectedInitiative)
                  ?.label || "",
            }}
          />
        </Container>
      </Box>

      <CreatePickerHost />
      <EnrollmentModalHost />
      <AssociateInitiativesHost />
      <UsersAndGroupsHost />
      <EditionEnrollmentHost />
      <SessionWizardHost />
      <ToastHostCst />
    </Box>
  );
};

export default CorporateDetailV2Layout;
